import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { connect, ConnectedProps } from "react-redux";
import {
  $Checkbox,
  $Radio,
  $TableTree,
  $Tooltip,
  $Skeleton,
  $Empty,
  $Switch,
  $Drawer,
  $Select,
  $Button,
  ActivityExecution,
  $Dropdown,
  $Input,
  IOnFilter,
  IOnSort,
  $Divider,
  $Popover,
  $AsyncInput,
} from "us.common/components";
import {
  ImportOutlined,
  ReloadOutlined,
  UpOutlined,
  DownOutlined,
} from "us.icons";
import { useParams } from "react-router-dom";
import { IFollowUpResult } from "us.collection.followup/components/FollowUps/Components/FollowUpResult/Interfaces";
import { followUps } from "us.collection.followup/actions";
import {
  FollowUpEntityType,
  FollowUpUniqueCol,
  FollowUpAction,
  FollowUpType,
  FilterType,
  LastAction,
  BMDData,
  MyClaimsBMDData,
  PermissionType,
} from "us.collection.followup/constants";
import { FollowUpActionHistory } from "us.collection.followup/components/FollowUps/Components";
import { ActionButton } from "us.collection.followup/components";
import { useSignalRTriggers } from "us.common/SignalRProvider";
import { NotificationTriggerType } from "us.common/constants/Component/Notification";
import { ExecutionSource, Mode } from "us.common/constants";
import { setPendingFollowups } from "us.collection.followup/functions";
import { followUpFilters } from "us.collection.followup/reducers/FollowUps/State/FollowUpFilters";
import { ALL_REASONS_ID, FollowupOrderType } from "./Constants";
import { useFormikContext } from "formik";
import { VList } from "virtuallist-antd";
import { RootState } from "us.helper/types";
import {
  getSavedFilters,
  isCommonFollowup,
  getSelectedFilterName,
  isFilterNameDuplicating,
  isAddressUnknownFollowup,
} from "./Functions";
import { SaveFollowUpFilters } from "./Repository";
import { getCurrentUserEmail } from "us.helper/utility";
import * as Azure from "us.helper/azure";
import { checkFilterPermission } from "us.collection.followup/functions";

const {
  saveFilter,
  result,
  filters,
  historyDrawer,
  reImport,
  lastActions,
  activity,
  action,
  bmd,
  columns,
  tableFilters,
  tableSorters,
} = followUps;

const FollowUpResult: React.FC<PropsFromRedux & IFollowUpResult> = (props) => {
  const {
    list,
    result,
    columns,
    followUpReasons,
    maxFollowUpscount,
    isFollowupReImporting,
    filters,
    historyDrawer,
    followUpNavigation,
    tableFilters,
    tableSorters,
    tableDataWithTableFilters,
    isFollowUpFilterPopupVisible,
    isFollowUpFilterSaving,
    caseHandlers,
    AuthorizationProfile,
    setFilters,
    manageFollowUpActionHistoryDrawer,
    getFollowUpResult,
    reImportFollowUp,
    updateLastAction,
    updateFollowupsLastActions,
    getActivityBMD,
    setTableColumns,
    setTableFilters,
    setTableSorters,
    handleFilterPopover,
    saveFilters,
    reimportingFollowupId,
  } = props;
  const { t } = useTranslation();
  const [selectedRowKeys, setSelectedRowKeys] = useState<Array<any>>([]);
  const [executeAction, setExecuteAction] = useState<string>("");
  const { followUpId, filterId } =
    useParams<{ followUpId?: string; filterId?: string }>();
  const [isVisibleColumnsSelect, setIsVisibleColumnsSelect] =
    useState<boolean>(false);

  const scDropdown = useRef<any>(null);
  const { values, setFieldValue } = useFormikContext<any>();
  const { data, dataSource, isFetching } = result;
  const { entityType, followupData, followupType } = data;
  const {
    caseStates,
    entityType: entityTypeInFilter,
    userOwnedClaims,
    actionStatus,
    selectedReasons,
    selectedCaseHandlers,
    createdUser,
  } = filters;
  const { open, sentence, nextDueDate, court, close } = caseStates;
  const { isBack } = followUpNavigation;
  const { data: tableColumnData, tableColumnWithStates } = columns;
  const currentUser: any = new Azure.ADAuth().currentUser();

  const isAddressUnknown = useMemo(() => {
    return isAddressUnknownFollowup(list.data, followUpId);
  }, [list.data, followUpId]);

  const isCommonFollowUp = useMemo(() => {
    return isCommonFollowup(list.data, followUpId);
  }, [list.data, followUpId]);

  const assignedCaseHandlers = useMemo(() => {
    const filter = list.data.find((filter: any) => filter.filterId == filterId);
    if (filter && filter.caseHandlers) {
      return filter.caseHandlers.split(",").map(Number);
    }
    return [];
  }, [list.data, filterId]);

  const triggers = useMemo(
    () => [
      {
        name: NotificationTriggerType.ON_ACTIVITY_EXECUTION,
        callBack: (content: any) => {
          const { batchId } = JSON.parse(content);
          batchId && updateLastAction && updateLastAction({ batchId });
        },
      },
    ],
    [],
  );
  useSignalRTriggers(triggers);

  const currentUserEmail = useMemo(() => {
    return getCurrentUserEmail(
      currentUser,
      AuthorizationProfile?.authorizationProfile,
    );
  }, [AuthorizationProfile]);

  const hasUpdatePermission = useMemo(() => {
    return checkFilterPermission(
      PermissionType.UPDATE,
      AuthorizationProfile?.authorizationProfile?.featureList,
    );
  }, [AuthorizationProfile]);

  useEffect(() => {
    if (!isBack) {
      getActivityBMD &&
        getActivityBMD([
          {
            entityType: BMDData.EntityType,
            categoryName: BMDData.CategoryName,
            bmdName: BMDData.BMDName,
            displayKeyName: BMDData.DisplayKeyName,
          },
          {
            entityType: MyClaimsBMDData.EntityType,
            categoryName: MyClaimsBMDData.CategoryName,
            bmdName: MyClaimsBMDData.BMDName,
            displayKeyName: MyClaimsBMDData.DisplayKeyName,
          },
        ]);
    }
  }, []);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [dataSource]);

  // Update filter values and get followup result after selecting a filter or followup
  useEffect(() => {
    if (!isBack && list.data?.length > 0 && !list.isFetching) {
      if (filterId) {
        const savedFilters = getSavedFilters(
          list.data,
          filters,
          filterId,
          caseHandlers.data,
        );
        setFilters && setFilters(savedFilters);
        followUpId &&
          getFollowUpResult &&
          getFollowUpResult({
            followUpId,
            isSavedFilter: true,
            reasons: savedFilters?.selectedReasons,
            filterId,
          });
      } else {
        followUpId && getFollowUpResult && getFollowUpResult({ followUpId });
      }
    }
  }, [followUpId, filterId, list.data, caseHandlers.data]);

  useEffect(() => {
    if (isFollowUpFilterPopupVisible) {
      let caseHandlerList: any[] = selectedCaseHandlers;
      if (Array.isArray(caseHandlers.data) && caseHandlers.data.length > 0) {
        caseHandlerList = caseHandlerList.filter((selectedHandler: any) =>
          caseHandlers?.data.some(
            (caseHandler: any) => caseHandler.id == selectedHandler,
          ),
        );
      }
      setFilters &&
        setFilters({ ...filters, selectedCaseHandlers: caseHandlerList });
    }
  }, [caseHandlers.data, isFollowUpFilterPopupVisible]);

  useEffect(() => {
    document.addEventListener("mousedown", closeOpenDropdown);
  }, []);

  const handleSort: IOnSort = (sortData, dataSource, sorters) => {
    const dataWithSorters = sortData(dataSource);
    setTableSorters && setTableSorters({ sorters, dataWithSorters });
    return dataWithSorters;
  };

  const handleFilter: IOnFilter = (searchData, dataSource, filters) => {
    const dataWithFilters = searchData(dataSource);
    setTableFilters && setTableFilters({ filters, dataWithFilters });
    return dataWithFilters;
  };

  const followupActionHistoryClose = () => {
    manageFollowUpActionHistoryDrawer &&
      manageFollowUpActionHistoryDrawer({ isAction: false });
  };

  const onSelectChange = (selectedRowKeys: any) => {
    setSelectedRowKeys(selectedRowKeys);
  };

  const rowSelection = {
    onChange: onSelectChange,
    hideDefaultSelections: true,
  };

  const vComponents = useMemo(() => {
    return VList({
      resetTopWhenDataChange: false,
      height: "calc(100vh - 320px)", // same value for scroll-y
    });
  }, [dataSource]);

  const handleCaseStateFilters = (
    filter: any,
    value: string | boolean | any[],
  ) => {
    switch (filter) {
      case FilterType.Open:
      case FilterType.Sentence:
      case FilterType.NextDueDate:
      case FilterType.Court:
      case FilterType.Close:
        setFilters &&
          setFilters({
            ...filters,
            caseStates: {
              ...caseStates,
              [`${filter}`]: value,
            },
          });
        break;
      case FilterType.SearchText:
      case FilterType.UserOwnedClaims:
        (entityType == FollowUpEntityType.CASE ||
          entityType == FollowUpEntityType.INVOICE) &&
          setFilters &&
          setFilters({
            ...filters,
            [`${filter}`]: value,
          });
        break;
      case FilterType.EntityType:
      case FilterType.ActionStatus:
        setFilters &&
          setFilters({
            ...filters,
            [`${filter}`]: value,
          });
      case FilterType.Reasons:
        setFilters &&
          setFilters({
            ...filters,
            [`${filter}`]: value,
          });
      case FilterType.CaseHandlers:
        setFilters &&
          setFilters({
            ...filters,
            [`${filter}`]: value,
          });
      default:
        setTableColumns &&
          setTableColumns({
            filter,
            value,
          });
        break;
    }
  };

  const resetFilters = () => {
    setFieldValue("searchText", "");
    setFilters &&
      setFilters({ ...followUpFilters, entityType: FollowUpEntityType.CASE });
    getFollowUpResult &&
      getFollowUpResult({ followUpId, isSavedFilter: true, filterId });
  };

  const overLayElement: HTMLElement = document.getElementById(
    "column-dropdown",
  ) as HTMLElement;

  const closeOpenDropdown = (e: any) => {
    if (scDropdown.current?.contains(e.target)) {
      return;
    }
    setIsVisibleColumnsSelect(false);
  };

  /**
   * @function
   * @description Handles reason select from the list
   */
  const handleReasonSelect = (selectedReasonId: number | string) => {
    if (selectedReasonId == ALL_REASONS_ID) {
      handleCaseStateFilters("selectedReasons", [ALL_REASONS_ID]);
      getFollowUpResult &&
        getFollowUpResult({ followUpId, isSavedFilter: true, filterId });
    } else {
      const reasonsData = [...selectedReasons, selectedReasonId].filter(
        (reasonId: number) => reasonId !== ALL_REASONS_ID,
      );
      handleCaseStateFilters("selectedReasons", reasonsData);
      getFollowUpResult &&
        getFollowUpResult({
          followUpId,
          reasons: reasonsData,
          isSavedFilter: true,
          filterId,
        });
    }
  };

  /**
   * @function
   * @description Handles reason Deselect from the list
   */
  const handleReasonDeselect = (selectedReasonId: number | string) => {
    const reasonsData = selectedReasons.filter(
      (reasonId: number) => reasonId !== selectedReasonId,
    );
    if (reasonsData.length > 0) {
      handleCaseStateFilters("selectedReasons", reasonsData);
      getFollowUpResult &&
        getFollowUpResult({
          followUpId,
          reasons: reasonsData,
          isSavedFilter: true,
          filterId,
        });
    }
  };
  /**
   * @function
   * @description Handles caseHandler Deselect from the list
   */
  const handleCaseHandlerDeselect = (selectedCaseHandler: string) => {
    const selectedHandlerList = selectedCaseHandlers.filter(
      (id: string) => id !== selectedCaseHandler,
    );
    if (selectedHandlerList?.length > 0) {
      handleCaseStateFilters("selectedCaseHandlers", selectedHandlerList);
    }
  };

  /**
   * @function
   * @description Handles filter saving functionality
   */
  const handleSaveFilter = (filterName: string, filterId?: string) => {
    const saveParams = SaveFollowUpFilters.call({
      ...filters,
      followUpId,
      filterId,
      filterName,
      selectedCaseHandlers,
      currentUserEmail,
    });
    saveFilters && saveFilters(saveParams);
  };

  /**
   * @function
   * @description Handles reimport followup
   */
  const handleReImportFollowup = () => {
    if (isCommonFollowUp) {
      reImportFollowUp &&
        reImportFollowUp({
          followUpId,
          isSavedFilter: true,
          reasons: selectedReasons,
          filterId,
        });
    } else {
      reImportFollowUp &&
        reImportFollowUp({
          followUpId,
        });
    }
  };

  /**
   * @function
   * @description Handles re-ordering of options based on selected values
   */
  const reorderOptions = (
    options: any[],
    selectedValues: any[],
    orderType: FollowupOrderType,
  ) => {
    if (Array.isArray(options) && options.length > 0) {
      if (orderType == FollowupOrderType.REASON) {
        const selectedOptions = options.filter((option) =>
          selectedValues.includes(option.reasonId),
        );
        const unselectedOptions = options.filter(
          (option) => !selectedValues.includes(option.reasonId),
        );
        return [...selectedOptions, ...unselectedOptions];
      } else {
        const selectedOptions = options.filter((option) =>
          selectedValues.includes(option.id),
        );
        const unselectedOptions = options.filter(
          (option) => !selectedValues.includes(option.id),
        );
        return [...selectedOptions, ...unselectedOptions];
      }
    } else {
      return options;
    }
  };

  return (
    <>
      {followupData.length === 0 &&
        !isFetching &&
        !list.isFetching &&
        !isCommonFollowUp && (
          <div className="fl-container">
            <div className="flc-body">
              <div className="followup-dashboard-msg">
                <$Empty
                  className="pt-5"
                  description={`${t(
                    "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.NO_DATA",
                  )}`}
                />
                {followupType != FollowUpType.STATIC_FOLLOW_UP && (
                  <div className="mt-4 text-center">
                    <$Button
                      type="dashed"
                      size="small"
                      icon={<ImportOutlined />}
                      loading={
                        isFollowupReImporting &&
                        reimportingFollowupId == followUpId
                      }
                      onClick={handleReImportFollowup}
                      className="mr-1 ml-3"
                    >
                      {t(
                        "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.RE_IMPORT_FOLLOWUP",
                      )}
                    </$Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      {(followupData.length > 0 ||
        isFetching ||
        list.isFetching ||
        isCommonFollowUp) && (
        <div className="fl-container">
          {(isCommonFollowUp ? true : !isFetching && !list.isFetching) && (
            <div className="flc-header">
              <div className="flex-grow-1 d-flex align-items-center">
                <$Input
                  name="searchText"
                  size="small"
                  allowClear={true}
                  disabled={isFetching || list.isFetching}
                  placeholder={t(
                    "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.SEARCH_IN_LIST",
                  )}
                  onChange={(textObj: any) => {
                    setFieldValue("searchText", textObj.target?.value, false);
                    handleCaseStateFilters("searchText", textObj.target?.value);
                  }}
                  style={{
                    width: 150,
                  }}
                  data-testid="searchText-input"
                />
                <$Tooltip
                  title={t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.STATUS")}
                >
                  <$Select
                    name="actionStatus"
                    className="ml-2"
                    style={{
                      width: isCommonFollowUp ? 130 : 180,
                    }}
                    optionText="label"
                    optionValue="value"
                    defaultValue="all"
                    disabled={isFetching || list.isFetching}
                    value={actionStatus}
                    allOption={true}
                    placeholder={t(
                      "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.LAST_ACTIVITY_EXECUTION",
                    )}
                    options={[
                      {
                        label: t(
                          "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.SUCCEED",
                        ),
                        value: LastAction.EXECUTED,
                      },
                      {
                        label: t(
                          "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.PENDING",
                        ),
                        value: LastAction.PENDING,
                      },
                      {
                        label: t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.FAILED"),
                        value: LastAction.FAILED,
                      },
                      {
                        label: t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.OTHER"),
                        value: LastAction.OTHER,
                      },
                    ]}
                    onChange={(status: string) => {
                      handleCaseStateFilters("actionStatus", status);
                    }}
                  />
                </$Tooltip>
                <$Divider type="vertical" className="mx-3" />
                <div
                  className={`d-flex align-items-center flc-saving-filters ${
                    isCommonFollowUp ? "flc-saving-filters-heighlight" : ""
                  }`}
                >
                  {(isCommonFollowUp ? true : !isFetching) &&
                    [
                      FollowUpEntityType.CASE,
                      FollowUpEntityType.INVOICE,
                    ].includes(entityType) && (
                      <div>
                        <$Tooltip
                          title={t("US.COLLECTION.COMMON:COMMON.OPEN")}
                          placement="top"
                        >
                          <span>
                            <$Checkbox
                              name="open"
                              checked={open}
                              className="ml-0 mr-0 checkbox-case-open"
                              disabled={isFetching || list.isFetching}
                              onChange={(e: any) =>
                                handleCaseStateFilters("open", e.target.checked)
                              }
                              data-testid="searchFilter-caseOpen"
                            />
                          </span>
                        </$Tooltip>
                        <$Tooltip
                          title={t("US.COLLECTION.COMMON:COMMON.SENTENCE")}
                        >
                          <span>
                            <$Checkbox
                              name="sentence"
                              checked={sentence}
                              disabled={isFetching || list.isFetching}
                              className="ml-1 mr-0 checkbox-case-sentence"
                              onChange={(e: any) =>
                                handleCaseStateFilters(
                                  "sentence",
                                  e.target.checked,
                                )
                              }
                              data-testid="searchFilter-caseSentence"
                            />
                          </span>
                        </$Tooltip>
                        <$Tooltip
                          title={t("US.COLLECTION.COMMON:COMMON.NEXT_DUE_DATE")}
                        >
                          <span>
                            <$Checkbox
                              name="nextDueDate"
                              checked={nextDueDate}
                              disabled={isFetching || list.isFetching}
                              className="ml-1 mr-0 checkbox-case-nextduedate"
                              onChange={(e: any) =>
                                handleCaseStateFilters(
                                  "nextDueDate",
                                  e.target.checked,
                                )
                              }
                              data-testid="searchFilter-nextDueDate"
                            />
                          </span>
                        </$Tooltip>
                        <$Tooltip
                          title={t("US.COLLECTION.COMMON:COMMON.COURT")}
                        >
                          <span>
                            <$Checkbox
                              name="court"
                              checked={court}
                              disabled={isFetching || list.isFetching}
                              className="ml-1 mr-0 checkbox-case-court"
                              onChange={(e: any) =>
                                handleCaseStateFilters(
                                  "court",
                                  e.target.checked,
                                )
                              }
                              data-testid="searchFilter-caseCourt"
                            />
                          </span>
                        </$Tooltip>
                        <$Tooltip
                          title={t("US.COLLECTION.COMMON:COMMON.CLOSE")}
                        >
                          <span>
                            <$Checkbox
                              name="close"
                              checked={close}
                              disabled={isFetching || list.isFetching}
                              className="ml-1 mr-0 checkbox-case-close"
                              onChange={(e: any) =>
                                handleCaseStateFilters(
                                  "close",
                                  e.target.checked,
                                )
                              }
                              data-testid="searchFilter-caseClose"
                            />
                          </span>
                        </$Tooltip>
                      </div>
                    )}

                  <$Radio
                    name="entityType"
                    size="small"
                    className="ml-2"
                    disabled={isFetching || list.isFetching}
                    options={
                      entityType == FollowUpEntityType.CASE
                        ? [
                            {
                              label: t(
                                `US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.CASE`,
                              ),
                              value: FollowUpEntityType.CASE,
                              dataTestId: `entityType-${FollowUpEntityType.CASE}-radio`,
                            },
                            {
                              label: t(
                                `US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.INVOICE`,
                              ),
                              value: FollowUpEntityType.INVOICE,
                              dataTestId: `entityType-${FollowUpEntityType.INVOICE}-radio`,
                            },
                          ]
                        : [
                            {
                              label: t(
                                `US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.${entityType.toUpperCase()}`,
                              ),
                              value: entityType,
                              dataTestId: `entityType-${entityType}-radio`,
                            },
                          ]
                    }
                    radioButton="true"
                    optionValue="value"
                    optionContent={(item: any) => item.label}
                    value={entityTypeInFilter}
                    buttonStyle="solid"
                    onChange={(e: any) =>
                      entityTypeInFilter != e.target.value &&
                      handleCaseStateFilters("entityType", e.target.value)
                    }
                    style={{
                      marginBottom: 7,
                    }}
                  />
                  {isCommonFollowUp && (
                    <div
                      className={`flc-dropdown-container ${
                        isFetching ||
                        list.isFetching ||
                        followUpReasons.isFetching
                          ? " disabled"
                          : ""
                      }`}
                    >
                      <label className="flc-reasons-label ml-2 px-2">
                        {t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.REASONS")}
                      </label>
                      <$Select
                        name="selectedReasons"
                        className="flc-select-reasons"
                        style={{
                          width: 220,
                        }}
                        dropdownMatchSelectWidth={false}
                        mode="multiple"
                        optionText="reasonName"
                        optionValue="reasonId"
                        defaultValue={-1}
                        value={selectedReasons}
                        allOption={true}
                        maxTagCount="responsive"
                        placeholder={t(
                          "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.REASONS",
                        )}
                        options={reorderOptions(
                          followUpReasons.data,
                          selectedReasons,
                          FollowupOrderType.REASON,
                        )}
                        onSelect={handleReasonSelect}
                        onDeselect={handleReasonDeselect}
                        onSearchBy={["reasonName"]}
                      />
                    </div>
                  )}
                  {(isCommonFollowUp ? true : !isFetching) &&
                    [
                      FollowUpEntityType.CASE,
                      FollowUpEntityType.INVOICE,
                    ].includes(entityType) && (
                      <div className="d-flex align-items-start ml-2">
                        <div
                          style={{
                            marginTop: -1,
                          }}
                        >
                          <$Switch
                            size="small"
                            name="userOwnedClaims"
                            onChange={(isChecked: boolean) =>
                              handleCaseStateFilters(
                                "userOwnedClaims",
                                isChecked,
                              )
                            }
                            checked={userOwnedClaims}
                            disabled={isFetching || list.isFetching}
                          />
                        </div>

                        <div className="pl-2" style={{ marginTop: 1 }}>
                          <div>
                            {t(
                              "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.MY_CLAIMS",
                            )}
                          </div>
                        </div>
                        {isCommonFollowUp && (
                          <>
                            <div className="pl-2">
                              <$Divider className="mx-0" type="vertical" />
                            </div>
                            <>
                              <$Button
                                size="small"
                                type="link"
                                onClick={resetFilters}
                              >
                                {t("US.COLLECTION.COMMON:COMMON.RESET")}
                              </$Button>
                            </>
                            <div className="pl-2">
                              <$Popover
                                placement="bottomRight"
                                open={
                                  isFollowUpFilterPopupVisible &&
                                  hasUpdatePermission
                                }
                                onOpenChange={(isVisible) => {
                                  if (isVisible) {
                                    setFieldValue(
                                      "saveFilterName",
                                      getSelectedFilterName(
                                        list.data,
                                        filterId,
                                      ),
                                    );
                                  }
                                  handleFilterPopover &&
                                    handleFilterPopover({});
                                  handleCaseStateFilters(
                                    "selectedCaseHandlers",
                                    assignedCaseHandlers,
                                  );
                                }}
                                content={
                                  <div>
                                    <div>
                                      <label className="ant-form-item-required ">
                                        {t(
                                          "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.FILTER_NAME",
                                        )}
                                      </label>
                                      <$AsyncInput
                                        name="saveFilterName"
                                        size="small"
                                        maxLength={100}
                                        style={{ width: "300px" }}
                                        disabled={isFollowUpFilterSaving}
                                        isValid={
                                          isFollowUpFilterPopupVisible
                                            ? !isFilterNameDuplicating(
                                                list.data,
                                                values?.saveFilterName,
                                                filterId,
                                                true,
                                              )
                                            : true
                                        }
                                        asyncError={t(
                                          "US.COLLECTION.VALIDATIONS:REQUIRED.DUPLICATE_FILTER_NAME",
                                        )}
                                      />
                                    </div>
                                    <div>
                                      <div className="mt-3">
                                        <label className="flc-reasons-label  px-2">
                                          {t(
                                            "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.ASSIGN_TO",
                                          )}
                                        </label>
                                      </div>
                                      <$Select
                                        name="caseHandlers"
                                        className="flc-select-reasons"
                                        style={{
                                          width: 220,
                                        }}
                                        mode="multiple"
                                        optionText="displayName"
                                        optionValue="id"
                                        defaultValue={-1}
                                        value={selectedCaseHandlers}
                                        allOption={false}
                                        maxTagCount="responsive"
                                        placeholder={t(
                                          "US.COLLECTION.COMMON:COMMON.USER",
                                        )}
                                        options={reorderOptions(
                                          caseHandlers.data,
                                          selectedCaseHandlers,
                                          FollowupOrderType.CASE_HANDLER,
                                        )}
                                        onSelect={(selection) => {
                                          selectedCaseHandlers
                                            ? handleCaseStateFilters(
                                                "selectedCaseHandlers",
                                                [
                                                  ...selectedCaseHandlers,
                                                  selection,
                                                ],
                                              )
                                            : handleCaseStateFilters(
                                                "selectedCaseHandlers",
                                                [selection],
                                              );
                                        }}
                                        onDeselect={handleCaseHandlerDeselect}
                                        onSearchBy={["displayName"]}
                                      />
                                    </div>
                                    <div className="d-flex justify-content-end mt-3 mb-1">
                                      {filterId && (
                                        <$Button
                                          className="mr-2"
                                          disabled={
                                            isFollowUpFilterSaving ||
                                            values?.saveFilterName == "" ||
                                            isFilterNameDuplicating(
                                              list.data,
                                              values?.saveFilterName,
                                              filterId,
                                            )
                                          }
                                          onClick={() =>
                                            handleSaveFilter(
                                              values?.saveFilterName,
                                              "-1",
                                            )
                                          }
                                          type="primary"
                                          size="small"
                                        >
                                          {t(
                                            "US.COLLECTION.COMMON:COMMON.SAVE_AS",
                                          )}
                                        </$Button>
                                      )}
                                      <$Button
                                        className="mr-2"
                                        disabled={
                                          isFollowUpFilterSaving ||
                                          values?.saveFilterName == "" ||
                                          isFilterNameDuplicating(
                                            list.data,
                                            values?.saveFilterName,
                                            filterId,
                                            true,
                                          ) ||
                                          (createdUser &&
                                            createdUser != currentUserEmail)
                                        }
                                        onClick={() =>
                                          handleSaveFilter(
                                            values?.saveFilterName,
                                            filterId,
                                          )
                                        }
                                        type="primary"
                                        size="small"
                                      >
                                        {t("US.COLLECTION.COMMON:COMMON.SAVE")}
                                      </$Button>
                                      <$Button
                                        size="small"
                                        onClick={() =>
                                          handleFilterPopover &&
                                          handleFilterPopover({})
                                        }
                                      >
                                        {t(
                                          "US.COLLECTION.COMMON:COMMON.CANCEL",
                                        )}
                                      </$Button>
                                    </div>
                                  </div>
                                }
                                trigger="click"
                              >
                                {!hasUpdatePermission ? (
                                  <$Tooltip
                                    title={t(
                                      "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.PERMISSION_DENIED",
                                    )}
                                  >
                                    <$Button
                                      type="default"
                                      size="small"
                                      loading={isFollowUpFilterSaving}
                                      disabled={
                                        isFollowUpFilterSaving ||
                                        isFetching ||
                                        list.isFetching ||
                                        followUpReasons.isFetching ||
                                        !hasUpdatePermission
                                      }
                                    >
                                      {t(
                                        "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.SAVE_FILTER",
                                      )}
                                    </$Button>
                                  </$Tooltip>
                                ) : (
                                  <$Button
                                    type="default"
                                    size="small"
                                    loading={isFollowUpFilterSaving}
                                    disabled={
                                      isFollowUpFilterSaving ||
                                      isFetching ||
                                      list.isFetching ||
                                      followUpReasons.isFetching ||
                                      !hasUpdatePermission
                                    }
                                  >
                                    {t(
                                      "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.SAVE_FILTER",
                                    )}
                                  </$Button>
                                )}
                              </$Popover>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                </div>
              </div>
              <div>
                {followupType != FollowUpType.STATIC_FOLLOW_UP && !isFetching && (
                  <$Tooltip
                    title={t(
                      "US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.RE_IMPORT_FOLLOWUP",
                    )}
                  >
                    <$Button
                      type="dashed"
                      size="small"
                      icon={<ImportOutlined />}
                      loading={
                        isFollowupReImporting &&
                        reimportingFollowupId == followUpId
                      }
                      onClick={() =>
                        reImportFollowUp &&
                        reImportFollowUp({
                          followUpId,
                        })
                      }
                      className="mr-1 ml-3"
                    />
                  </$Tooltip>
                )}
                <$Tooltip title={t("COMMON.REFRESH")}>
                  <$Button
                    type="dashed"
                    size="small"
                    disabled={isFetching}
                    icon={<ReloadOutlined />}
                    onClick={() => {
                      if (followUpId && getFollowUpResult) {
                        if (isCommonFollowUp) {
                          getFollowUpResult({
                            followUpId,
                            isSavedFilter: true,
                            reasons: selectedReasons,
                            filterId,
                          });
                        } else {
                          getFollowUpResult({ followUpId });
                        }
                      }
                    }}
                  />
                </$Tooltip>
              </div>
            </div>
          )}
          <div className="flc-body">
            {!isFetching && !list.isFetching && (
              <div
                className="d-flex align-items-start justify-content-between"
                data-testid="selectedFollowUpCount-div"
              >
                <div className="flex-grow-1 d-flex align-items-start">
                  <div className="flc-count mr-5">
                    <span>
                      {selectedRowKeys.length}{" "}
                      {t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.SELECTED")}
                    </span>
                    <span className="bui-label mx-2">/</span>
                    <span className="bui-label">
                      {dataSource?.length}{" "}
                      {t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.TOTAL")}
                    </span>
                  </div>
                  <ActivityExecution
                    mode={Mode.Initial}
                    isHideTable={false}
                    isDisabled={selectedRowKeys.length === 0}
                    isBulkExecutionEnabled={true}
                    isGroupActivityOptions={true}
                    isActivityOptionWithCode={true}
                    isHideDatePicker={false}
                    activityAPIRequest={{
                      endPoint: `activitiesforfollowup/${followUpId}/${false}`,
                    }}
                    executeActivityEndPoint="bulkactivity"
                    groupingProperty={{
                      groupingLabel: "activityType",
                      groupingValue: "activityType",
                      tableGrouping: "type",
                      optionsGrouping: "activityType",
                    }}
                    isPopConfirmEnabled={true}
                    isPopConfirmEnabledDropDown={true}
                    propConfirmDetail={{
                      title:
                        "DO_YOU_WANT_TO_REMOVE_RESULTS_FROM_THE_FOLLOWUP_AFTER_SUCCESSFUL_EXECUTION",
                      isOnCancelSubmit: true,
                    }}
                    filter={entityTypeInFilter}
                    reduxActivities={[
                      {
                        action: action.execute,
                      },
                      {
                        action: updateFollowupsLastActions,
                        method: setPendingFollowups,
                      },
                    ]}
                    isActionExecution={true}
                    selectedEntities={selectedRowKeys}
                    externalData={dataSource}
                    maxEntityCountExecuteAtOnce={maxFollowUpscount}
                    bulkExecutionSource={ExecutionSource.FollowUpBulkExecution}
                    isAddressUnknown={isAddressUnknown}
                    followUpId={followUpId}
                  />
                </div>
                <div
                  className="d-flex align-items-center"
                  id="column-dropdown"
                  ref={scDropdown}
                >
                  <$Dropdown
                    overlay={
                      <div className="ant-dropdown-menu">
                        <div className="p-2">
                          {tableColumnWithStates &&
                            tableColumnWithStates.map(
                              (
                                header: {
                                  key: boolean;
                                },
                                index: number,
                              ) => {
                                const [key, value] = Object.entries(header)[0];
                                return (
                                  key != FollowUpUniqueCol.FOLLOWUP_ID &&
                                  key !=
                                    FollowUpUniqueCol.FOLLOWUP_CASE_STATE &&
                                  key != FollowUpUniqueCol.CASE_TYPE && (
                                    <div className="mb-1">
                                      <$Checkbox
                                        name={key}
                                        onChange={(column: any) =>
                                          handleCaseStateFilters(
                                            key,
                                            column.target.checked,
                                          )
                                        }
                                        checked={value ? value : false}
                                      >
                                        {key == FollowUpUniqueCol.CASE_NO
                                          ? "Caseid"
                                          : key == FollowUpUniqueCol.EX_CASE_NO
                                          ? "Caseno"
                                          : key}
                                      </$Checkbox>
                                    </div>
                                  )
                                );
                              },
                            )}
                        </div>
                      </div>
                    }
                    getPopupContainer={() => overLayElement}
                    placement="bottomRight"
                    trigger={["click"]}
                    arrow
                    overlayClassName=""
                    open={isVisibleColumnsSelect}
                  >
                    <$Button
                      size="small"
                      onClick={() =>
                        setIsVisibleColumnsSelect(!isVisibleColumnsSelect)
                      }
                      className="flc-select-columns"
                    >
                      {t("US.COLLECTION.COMMON:COMMON.SELECT_COLUMNS")}
                      <small className="ml-2">
                        {isVisibleColumnsSelect ? (
                          <UpOutlined />
                        ) : (
                          <DownOutlined />
                        )}
                      </small>
                    </$Button>
                  </$Dropdown>
                </div>
              </div>
            )}

            <div>
              <$Skeleton
                loading={isFetching || list.isFetching}
                active
                paragraph={{
                  rows: 2,
                }}
              >
                <$TableTree
                  rowKey={(record: any) => {
                    return record.hasOwnProperty("children")
                      ? record.key
                      : record.Followupid;
                  }}
                  data={dataSource}
                  size="small"
                  className="mt-3"
                  onSort={handleSort}
                  onFilter={handleFilter}
                  filterOnType={true}
                  resetOnSourceChange={true}
                  bordered
                  pagination={false}
                  scroll={{
                    x: 600,
                    y: `calc(100vh - ${
                      followupType != FollowUpType.DYNAMIC_FOLLOW_UP
                        ? "340px"
                        : "292px"
                    })`,
                  }}
                  columns={tableColumnData}
                  firstColSkipFilterProps={-1}
                  rowSelection={{
                    type: "checkbox",
                    ...rowSelection,
                  }}
                  onRow={() => {
                    return {
                      onDoubleClick: () => {},
                    };
                  }}
                  data-testid="followUpResult-table"
                  defaultFilters={tableFilters?.data}
                  defaultSorters={tableSorters?.data}
                  defaultData={tableDataWithTableFilters?.data}
                  components={vComponents}
                />
              </$Skeleton>
            </div>
          </div>

          {followupType != FollowUpType.DYNAMIC_FOLLOW_UP && (
            <div className="flc-footer">
              <div>
                <ActionButton
                  followUpId={followUpId}
                  followupdataIds={selectedRowKeys}
                  visible={FollowUpAction.POSTPONE === executeAction}
                  type={FollowUpAction.POSTPONE}
                  onVisibleChange={(visible: boolean) => {
                    setExecuteAction(visible ? FollowUpAction.POSTPONE : "");
                  }}
                  isCommonFollowUp={isCommonFollowUp}
                />
                <ActionButton
                  followUpId={followUpId}
                  followupdataIds={selectedRowKeys}
                  visible={FollowUpAction.REMOVE === executeAction}
                  type={FollowUpAction.REMOVE}
                  onVisibleChange={(visible: boolean) => {
                    setExecuteAction(visible ? FollowUpAction.REMOVE : "");
                  }}
                  isCommonFollowUp={isCommonFollowUp}
                />
                <ActionButton
                  followUpId={followUpId}
                  followupdataIds={selectedRowKeys}
                  visible={FollowUpAction.DONE === executeAction}
                  type={FollowUpAction.DONE}
                  onVisibleChange={(visible: boolean) => {
                    setExecuteAction(visible ? FollowUpAction.DONE : "");
                  }}
                  isCommonFollowUp={isCommonFollowUp}
                />
              </div>
            </div>
          )}
        </div>
      )}
      <$Drawer
        title={t("US.COLLECTION.FOLLOWUP:FOLLOWUP_HOME.ACTION_HISTORY")}
        width={"90%"}
        open={historyDrawer.isAction}
        onClose={followupActionHistoryClose}
        destroyOnClose
      >
        <FollowUpActionHistory onClose={followupActionHistoryClose} />
      </$Drawer>
    </>
  );
};

const mapStateToProps = (state: RootState) => {
  const { followUps, common, AuthorizationProfile } = state;
  const { currentDateFormat } = common;
  const {
    list,
    result,
    columns,
    filters,
    reasons,
    historyDrawer,
    maxFollowUpscount,
    isFollowupReImporting,
    followUpNavigation,
    tableFilters,
    tableSorters,
    tableDataWithTableFilters,
    reimportingFollowupId,
    isFollowUpFilterPopupVisible,
    isFollowUpFilterSaving,
    caseHandlers,
  } = followUps;
  return {
    list,
    result,
    columns,
    followUpReasons: reasons,
    filters: filters.data,
    historyDrawer,
    currentDateFormat,
    maxFollowUpscount,
    isFollowupReImporting,
    followUpNavigation,
    tableFilters,
    tableSorters,
    tableDataWithTableFilters,
    reimportingFollowupId,
    isFollowUpFilterPopupVisible,
    isFollowUpFilterSaving,
    caseHandlers,
    AuthorizationProfile,
  };
};

const mapDispatchToProps = {
  getFollowUpResult: result.get,
  setFilters: filters.set,
  manageFollowUpActionHistoryDrawer: historyDrawer.manage,
  reImportFollowUp: reImport.execute,
  updateLastAction: lastActions.get,
  setActivityExecutionAction: activity.set,
  actionExecute: action.execute,
  updateFollowupsLastActions: result.setLastActions,
  getActivityBMD: bmd.get,
  setTableColumns: columns.set,
  setTableFilters: tableFilters.set,
  setTableSorters: tableSorters.set,
  handleFilterPopover: saveFilter.handlePopover,
  saveFilters: saveFilter.save,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

export default connector(FollowUpResult);
